"use client";

import React, { useState } from "react";
import { Maximize2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const categories = ["All", "Dogs", "Cats", "Other"];
const galleryItems = [{
  id: 1,
  category: "Dogs",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687484/img/happy_golden_retriever.png",
  serviceType: "Max's Recovery",
  title: "Golden Retriever smiling after checkup"
}, {
  id: 2,
  category: "Cats",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687484/img/calico_cat_wellness.png",
  serviceType: "Luna's Wellness",
  title: "Calico cat during a routine exam"
}, {
  id: 3,
  category: "Other",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687484/img/rabbit_dental_care.png",
  serviceType: "Barnaby's Dental",
  title: "Rabbit receiving specialized care"
}, {
  id: 4,
  category: "Dogs",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687484/img/french_bulldog_puppy.png",
  serviceType: "First Vaccinations",
  title: "Puppy's first visit to City Pet Care"
}, {
  id: 5,
  category: "Cats",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687484/img/tabby_cat_senior.png",
  serviceType: "Senior Care",
  title: "Senior tabby cat resting comfortably"
}, {
  id: 6,
  category: "Dogs",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687484/img/border_collie_agility.png",
  serviceType: "Joint Health",
  title: "Active dog showing great mobility"
}, {
  id: 7,
  category: "Other",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687484/img/guinea_pig_nutrition.png",
  serviceType: "Nutritional Advice",
  title: "Guinea pig enjoying healthy snacks"
}, {
  id: 8,
  category: "Cats",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687484/img/siamese_cat_emergency.png",
  serviceType: "Emergency Success",
  title: "Siamese cat fully recovered"
}, {
  id: 9,
  category: "Dogs",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687484/img/labrador_dental_cleaning.png",
  serviceType: "Dental Hygiene",
  title: "Labrador with sparkling clean teeth"
}];
export default function TheFacesOfCityPetCare() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [lightboxImage, setLightboxImage] = useState<{
    src: string;
    alt: string;
  } | null>(null);
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: galleryRef,
    isVisible: isGalleryVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const filteredItems = galleryItems.filter(item => activeCategory === "All" || item.category === activeCategory);
  return <section data-section-id="222828" id="the-faces-of-city-pet-care" className="bg-background text-foreground py-24 px-6 md:px-12 lg:px-24 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`flex flex-col items-center text-center mb-16 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-default text-4xl md:text-5xl font-bold text-foreground mb-4 tracking-tight">
            The Faces of City Pet Care
          </h2>
          <p className="text-muted-foreground max-w-2xl text-lg font-default leading-relaxed">
            Meet some of our wonderful patients. Every photo tells a story of health, 
            happiness, and the special bond between pets and their families.
          </p>
        </div>

        {}
        <div className="flex justify-center mb-12">
          <Tabs defaultValue="All" onValueChange={setActiveCategory} className="w-full max-w-2xl">
            <TabsList className="w-full flex flex-wrap h-auto bg-transparent gap-2 justify-center">
              {categories.map((category, i) => <TabsTrigger key={category} data-index={i} value={category} className="rounded-lg px-6 py-2.5 text-sm font-medium transition-all data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=inactive]:bg-secondary data-[state=inactive]:text-secondary-foreground data-[state=inactive]:hover:bg-accent">
                  {category}
                </TabsTrigger>)}
            </TabsList>
          </Tabs>
        </div>

        {}
        <div ref={galleryRef} className={`columns-1 sm:columns-2 lg:columns-3 gap-6 space-y-6 transition-all duration-1000 delay-200 ease-out ${isGalleryVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {filteredItems.map((item, i) => <div key={item.id} data-index={i} className="relative group break-inside-avoid overflow-hidden rounded-2xl bg-card shadow-md">
              <img src={item.image} alt={item.title} className="w-full h-auto object-cover transition-transform duration-700 ease-in-out group-hover:scale-105" loading="lazy" />
              
              {}
              <div className="absolute inset-0 bg-primary/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center p-6 cursor-pointer backdrop-blur-sm" onClick={() => setLightboxImage({
            src: item.image,
            alt: item.title
          })}>
                <Maximize2 className="text-primary-foreground size-8 mb-4 transform scale-50 group-hover:scale-100 transition-transform duration-500" />
                <h3 className="text-primary-foreground font-default text-2xl font-semibold mb-6 text-center translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                  {item.serviceType}
                </h3>
                
                {}
                <div onClick={e => e.stopPropagation()} className="translate-y-4 group-hover:translate-y-0 transition-transform duration-500 delay-75">
                  <Button asChild variant="secondary" size="lg" className="rounded-lg px-8 shadow-lg">
                    <Link to="/WellnessServicesPage">View Services</Link>
                  </Button>
                </div>
              </div>
            </div>)}
        </div>
      </div>

      {}
      <Dialog open={!!lightboxImage} onOpenChange={open => !open && setLightboxImage(null)}>
        <DialogContent className="max-w-5xl w-[95vw] p-1 bg-transparent border-none" showCloseButton={true}>
          <DialogTitle className="sr-only">
            {lightboxImage?.alt || "Pet Story Preview"}
          </DialogTitle>
          {lightboxImage && <div className="relative w-full flex items-center justify-center overflow-hidden rounded-2xl bg-background/40 backdrop-blur-md">
              <img src={lightboxImage.src} alt={lightboxImage.alt} className="max-w-full max-h-[85vh] w-auto h-auto object-contain rounded-2xl" />
            </div>}
        </DialogContent>
      </Dialog>
    </section>;
}